import { setClassName } from '@change-corgi/core/error';

import { PageDataError } from '#src/shared/error';

import type { PsfPromoteOrShareDataQuery } from '../api.graphql';
import type { PsfPromoteOrShareRecruiterQuery } from '../recruiter.graphql';
import type { PsfSocialStarterDataQuery } from '../socialStarter.graphql';

export type PsfPromoteOrSharePageDataErrorCode =
	| 'psf_promote_or_share_page_data_not_found'
	| 'psf_promote_or_share_page_missing_promotion_stats';
export class PsfPromoteOrSharePageDataError extends PageDataError<PsfPromoteOrSharePageDataErrorCode> {}
setClassName(PsfPromoteOrSharePageDataError, 'PsfPromoteOrSharePageDataError');

export type Recruiter = PsfPromoteOrShareRecruiterQuery['recruiter'];
export type Petition = NonNullable<PsfPromoteOrShareDataQuery['petition']>;
export type Viewer = PsfPromoteOrShareDataQuery['viewer'];
export type PromotionStats = NonNullable<Petition['promotionStats']>;

export type FcmValues = PsfPromoteOrShareDataQuery['featureConfigs'];

export type HeroFeedItem = NonNullable<PsfPromoteOrShareDataQuery['petition']>['feeds']['heroFeed'][0];

/** Not exported: only combo's own experiment sub-types below need it. */
type ComboExperiments = PsfPromoteOrShareDataQuery['experiments'];
export type SocialStarterExperiments = PsfSocialStarterDataQuery['experiments'];
export type PromoteShareOnComboExperiment = ComboExperiments['promoteShareOnComboExperiment'];
export type PsfShareFirstSocialStarterExperiment = SocialStarterExperiments['psfShareFirstSocialStarterExperiment'];
/** @knip-ignore */
export type PsfComboCopyExperiment = ComboExperiments['psfComboCopyExperiment'];
/**
 * Resolved with `treat: false`: `useTreatFacebookPostSheetExperiment` treats it, and fires `$exposure`,
 * at the Facebook tap (CHANGE-99697).
 * @knip-ignore
 */
export type PsfPromoteOrShareFacebookPostSheetExperiment =
	ComboExperiments['psfPromoteOrShareFacebookPostSheetExperiment'];
// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: false`: `useNativeShareTitle` treats it, and fires `$exposure`, at the native
 * share tap (CHANGE-99262).
 * @knip-ignore
 */
export type NativeShareTitleExperiment = ComboExperiments['nativeShareTitleExperiment'];
// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: false`: `useTreatQrCodeExperiment` treats it, and fires `$exposure`, when the
 * share slide-up opens (CHANGE-99266).
 * @knip-ignore
 */
export type PsfPromoteOrShareQrCodeExperiment = ComboExperiments['psfPromoteOrShareQrCodeExperiment'];
// EXPERIMENT: psf_og_text_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: true`: the same request builds the share links, whose `/og/variant_n` segment
 * carries the arm (CHANGE-101426). PageBody fires `$exposure` on mount.
 * @knip-ignore
 */
export type PsfOgTextExperiment = ComboExperiments['psfOgTextExperiment'];

export type PsfPromoteOrSharePageData = {
	petition: Omit<Petition, 'bandits' | 'headlineBandits' | 'promotionStats' | 'tagsConnection'> & {
		promotionStats: PromotionStats;
		categoryTags: string[];
	};
	recruiter?: Recruiter;
	chipInAmount: number;
	chipInAmountLocalized: string;
	/** Currency `chipInAmount` is denominated in; carried to the share page with it. */
	chipInCurrencyCode: string;
	joinTheCommunityCustomConfig?: {
		promoterCount: number;
		promoterCountLocalized: string;
		contributionAmount: number;
		contributionAmountLocalized: string;
		additionalSignatures: number;
		additionalSignaturesLocalized: string;
	};
	promotionGoal: number;
	promotionGoalLocalized: string;
	hideRecruiterEnabled: boolean;
	isShareSlideUpEnabled: boolean;
	isShareAssetsEnabled: boolean;
	campaignActionsEnabled: boolean;
	whatsappHeadlineExperiment: {
		variation: 'control' | 'variant1' | 'variant2' | 'variant3' | 'variant4' | 'fake_control';
		name: string;
		variants: Partial<
			Readonly<
				Record<
					'variant1' | 'variant2' | 'variant3' | 'variant4' | 'fake_control',
					Readonly<{
						message: string;
					}>
				>
			>
		>;
	};
	signaturesFromShareCountEstimateLocalized: string;
	useDonationLanguage: boolean;
	advocateEntryPointButtonLabel: string;
	advocateEntryPointExperiment: {
		variation: ComboExperiments['advocateEntryPoint']['variation'];
		name: string;
	};
	advocateEntryPointCpDisabled: boolean;
	promoteShareOnComboExperiment: {
		variation: ComboExperiments['promoteShareOnComboExperiment']['variation'];
	};
	psfShareFirstSocialStarterExperiment: PsfShareFirstSocialStarterExperiment;
	channelAwareCtaChannels: FcmValues['channelAwareCtaChannels'];
	psfComboCopyExperiment: PsfComboCopyExperiment;
	psfPromoteOrShareFacebookPostSheetExperiment: PsfPromoteOrShareFacebookPostSheetExperiment;
	// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
	nativeShareTitleExperiment: NativeShareTitleExperiment;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	psfPromoteOrShareQrCodeExperiment: PsfPromoteOrShareQrCodeExperiment;
	// EXPERIMENT: psf_og_text_experiment — remove with experiment cleanup
	psfOgTextExperiment: PsfOgTextExperiment;
	/** Kill switch for the Facebook post sheet probe and experiment — see `useFacebookPostSheetEligibility`. */
	facebookPostSheetFcm: PsfPromoteOrShareDataQuery['featureConfigs']['facebookPostSheetFcm'];
	psfComboBanditVariation: 'static' | 'default' | null;
	// No displayable cover image: the petition-media slot is removed entirely.
	hideMediaSlot: boolean;
	// Raw data for PromoteForm normalization - avoids circular dependency
	promoteFormRawData?: {
		viewer: Viewer;
		featureConfigs: FcmValues;
	};
};
